import * as PIXI from 'pixi.js';

/**
 * Константы слоев для централизованного управления z-index
 * Все z-index значения должны браться отсюда, а не хардкодиться
 */
export const LAYER_DEPTHS = {
  // Основные слои (базовые контейнеры)
  BACKGROUND: 0,
  BACKGROUND_PARTICLES: 50, // Фоновые частицы (трейлы, атмосфера)
  ASTEROIDS: 75, // Астероиды поверх фоновых частиц
  GAME: 100,
  UI: 1000,
  PARTICLES: 2000,
  // Debug и overlay
  DEBUG: 9999,
} as const;

export type LayerDepth = (typeof LAYER_DEPTHS)[keyof typeof LAYER_DEPTHS];

// Расширенный тип для поддержки игровых слоев
export type ExtendedLayerDepth = LayerDepth | number;

/**
 * Менеджер слоев для управления z-index и контейнерами
 * Централизует всю логику работы со слоями
 */
export class LayerManager {
  private layers = new Map<ExtendedLayerDepth, PIXI.Container>();
  private parentContainer: PIXI.Container;

  constructor(parentContainer: PIXI.Container) {
    this.parentContainer = parentContainer;
    this.initializeLayers();
  }

  /**
   * Инициализация всех слоев
   */
  private initializeLayers(): void {
    // Создаем контейнеры для каждого слоя
    Object.values(LAYER_DEPTHS).forEach(depth => {
      const container = new PIXI.Container();
      container.zIndex = depth;
      this.layers.set(depth, container);
      this.parentContainer.addChild(container);
    });

    // Сортируем по z-index для правильного порядка отрисовки
    this.parentContainer.sortChildren();
  }

  /**
   * Регистрация дополнительных слоев (например, игровых)
   */
  registerAdditionalLayers(depths: ExtendedLayerDepth[]): void {
    depths.forEach(depth => {
      if (!this.layers.has(depth)) {
        const container = new PIXI.Container();
        container.zIndex = depth;
        this.layers.set(depth, container);
        this.parentContainer.addChild(container);
      }
    });
    // Пересортируем после добавления новых слоев
    this.parentContainer.sortChildren();
  }

  /**
   * Получить контейнер для указанного слоя
   */
  getLayer(depth: ExtendedLayerDepth): PIXI.Container {
    const container = this.layers.get(depth);
    if (!container) {
      throw new Error(`Layer with depth ${depth} not found!`);
    }
    return container;
  }

  /**
   * Добавить объект на указанный слой
   */
  addToLayer(depth: ExtendedLayerDepth, child: PIXI.Container): void {
    const container = this.getLayer(depth);
    container.addChild(child);
  }

  /**
   * Удалить объект с указанного слоя
   */
  removeFromLayer(depth: ExtendedLayerDepth, child: PIXI.Container): void {
    const container = this.getLayer(depth);
    if (container.children.includes(child)) {
      container.removeChild(child);
    }
  }

  /**
   * Переместить объект на другой слой
   */
  moveToLayer(
    fromDepth: ExtendedLayerDepth,
    toDepth: ExtendedLayerDepth,
    child: PIXI.Container
  ): void {
    this.removeFromLayer(fromDepth, child);
    this.addToLayer(toDepth, child);
  }

  /**
   * Получить все объекты на слое
   */
  getLayerChildren(depth: ExtendedLayerDepth): PIXI.Container[] {
    return this.getLayer(depth).children;
  }

  /**
   * Очистить слой
   */
  clearLayer(depth: ExtendedLayerDepth): void {
    const container = this.getLayer(depth);
    container.removeChildren();
  }

  /**
   * Установить видимость слоя
   */
  setLayerVisible(depth: ExtendedLayerDepth, visible: boolean): void {
    const container = this.getLayer(depth);
    container.visible = visible;
  }

  /**
   * Установить альфу слоя
   */
  setLayerAlpha(depth: ExtendedLayerDepth, alpha: number): void {
    const container = this.getLayer(depth);
    container.alpha = alpha;
  }

  /**
   * Получить информацию о всех слоях (для дебага)
   */
  getLayersInfo(): Array<{ depth: ExtendedLayerDepth; childrenCount: number; visible: boolean }> {
    return Array.from(this.layers.entries()).map(([depth, container]) => ({
      depth,
      childrenCount: container.children.length,
      visible: container.visible,
    }));
  }

  /**
   * Сортировать все слои по z-index
   */
  sortLayers(): void {
    this.parentContainer.sortChildren();
  }

  /**
   * Уничтожение менеджера слоев и очистка ресурсов
   */
  destroy(): void {
    // Очищаем все контейнеры
    this.layers.forEach(container => {
      container.removeChildren();
      if (container.parent) {
        container.parent.removeChild(container);
      }
      container.destroy({ children: true });
    });

    // Очищаем Map
    this.layers.clear();

    // Очищаем ссылку на родительский контейнер
    this.parentContainer = null as any;
  }
}
